/** C1 — upload the process server's certificate of service → OneDrive / Court; completes the certificate chase (G8 item). */
import { useState } from "react";
import { FileBadge2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { recordPetitionCertificate } from "@/lib/api";
import { Field, FileDrop, UploadProgress, errMsg, today, uploadAll } from "./petitionShared";

export default function PetitionCertificateDialog({ caseRow, onClose, onSaved }) {
  const [form, setForm] = useState({ filed_on: today(), notes: "" });
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    try {
      const refs = await uploadAll(files.map((f) => ({ file: f, kind: "other" })), setProgress);
      const r = await recordPetitionCertificate(caseRow.case_id, { ...form, files: refs });
      (r.warnings || []).forEach((w) => toast.warning(w));
      toast.success(`Certificate of service saved to OneDrive / Court${r.completed_actions ? " · certificate chase completed" : ""}`);
      onSaved?.(r);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); setProgress(null); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="petition-certificate-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2"><FileBadge2 className="w-4 h-4 text-violet-700" />Certificate of service — {caseRow.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <Field label="Certificate dated"><Input type="date" value={form.filed_on} max={today()} onChange={set("filed_on")} className="h-8 text-xs w-44" data-testid="certificate-date" /></Field>
          <Field label="Certificate file(s)"><FileDrop files={files} setFiles={setFiles} busy={busy} required label="Attach certificate" hint="PDF or photo from the process server (required)" testId="certificate-files" /></Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={set("notes")} className="text-xs" data-testid="certificate-notes" /></Field>
          <UploadProgress progress={progress} testId="certificate-upload-progress" />
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="certificate-cancel">Cancel</Button>
          <Button size="sm" className="bg-violet-700 hover:bg-violet-800" onClick={submit} disabled={busy || files.length === 0} data-testid="certificate-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileBadge2 className="w-3.5 h-3.5 mr-1" />}{busy ? (progress ? "Uploading…" : "Saving…") : "Save certificate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
